import { Timestamp } from "@bufbuild/protobuf";
import { isMethodSupported } from "@pocketsign/in-app-sdk";
import { requestStoreReview } from "@pocketsign/in-app-sdk/internal";
import { captureException } from "@sentry/react";
import { subDays } from "date-fns";
import {
  TimePaginationRequest,
  type TransactionGroup,
  Transaction_Type,
} from "schema/gen/es/chiikipoint/model/v2/model_pb";
import type { ConnectClient } from "../../libs/api";
import { AppError, ERROR_CODES } from "../../libs/errors";
import { sdk, shouldSkipMockSDK } from "../../libs/sdk";

const REVIEW_LOOKBACK_DAYS = 180;
const REVIEW_TARGET_TENANT_SLUGS = new Set(["miyagi", "hokkaido"]);

export async function requestStoreReviewAfterPayment(
  client: ConnectClient,
  tenantSlug: string,
  currentPayment: TransactionGroup,
) {
  try {
    if (!REVIEW_TARGET_TENANT_SLUGS.has(tenantSlug)) {
      return;
    }

    if (shouldSkipMockSDK() || !isMethodSupported(sdk, requestStoreReview)) {
      return;
    }

    if (!(await shouldRequestStoreReview(client, currentPayment))) {
      return;
    }

    const result = await requestStoreReview(sdk);
    if (result.result === "error") {
      captureException(
        new AppError(ERROR_CODES.REQUEST_STORE_REVIEW_FAILED, undefined, {
          sdkErrorNo: result.errno,
        }),
      );
    }
  } catch (error) {
    captureException(
      new AppError(ERROR_CODES.REQUEST_STORE_REVIEW_FAILED, undefined, {
        originalError: error,
      }),
      { extra: { originalError: error } },
    );
  }
}

async function shouldRequestStoreReview(
  client: ConnectClient,
  currentPayment: TransactionGroup,
): Promise<boolean> {
  const paidAt = currentPayment.transactionDetails
    .find(({ transaction }) => transaction?.type === Transaction_Type.PAYMENT)
    ?.transaction?.createdAt?.toDate();

  if (!paidAt) {
    return false;
  }

  const cutoff = subDays(paidAt, REVIEW_LOOKBACK_DAYS);
  // APIの下限は排他的。Postgresの時刻精度（1マイクロ秒）だけ引いて境界を含める。
  const limit = Timestamp.fromDate(new Date(cutoff.getTime() - 1));
  limit.nanos += 999_000;
  const { transactionGroups } = await client.getMyTransactions({
    pagination: new TimePaginationRequest({
      offset: Timestamp.fromDate(paidAt),
      limit,
    }),
  });

  const hasPreviousPayment = transactionGroups.some(
    ({ id, transactionDetails }) =>
      id !== currentPayment.id &&
      transactionDetails.some(
        ({ transaction }) => transaction?.type === Transaction_Type.PAYMENT,
      ),
  );

  return !hasPreviousPayment;
}
